import { useQuery } from "@tanstack/react-query";
import { erp } from "@/lib/erp";
import type { SalesInvoice } from "@/lib/erp";
import type { SalesInvoiceListParams } from "@/lib/erp/salesInvoices";
import { SALES_INVOICE_LIST_FIELDS } from "@/lib/erp/salesInvoices";
import type { FilterDescriptor } from "@/lib/erp";
import type { FrappeFilterArray } from "@/lib/erp/filterMapper";
import { useDocTypeListWithPagination } from "@/features/common/api/useDocTypeListWithPagination";

/**
 * Parameters for useSalesInvoiceListWithPagination
 *
 * Note: filterDescriptors is the preferred way to pass filters.
 * The preMappedFilters param is for legacy/advanced use only (pre-mapped Frappe filters).
 */
export type SalesInvoiceListHookParams = Omit<SalesInvoiceListParams, "filters"> & {
  /**
   * FilterDescriptor[] to be converted to Frappe format internally.
   * This is the preferred way to pass filters for normal sales invoice use.
   */
  filterDescriptors?: FilterDescriptor[];
  /**
   * Legacy/advanced: pass pre-mapped Frappe filter array.
   * Prefer filterDescriptors in new code.
   *
   * @deprecated Use filterDescriptors instead. This is kept for backwards compatibility.
   */
  preMappedFilters?: FrappeFilterArray;
  /**
   * @deprecated Use preMappedFilters instead for clarity. Kept for backwards compatibility.
   */
  filters?: FrappeFilterArray;
  /**
   * Optional: fields to fetch from backend.
   * If not provided, defaults to SALES_INVOICE_LIST_FIELDS.
   * If provided, overrides the default fields list.
   */
  fields?: string[];
};

/**
 * Hook for fetching sales invoices with server-side pagination and filtering.
 *
 * This is a thin wrapper around the generic useDocTypeListWithPagination hook,
 * providing Sales Invoice–specific defaults (doctype: "Sales Invoice",
 * searchFields: ["name", "customer"]).
 *
 * IMPORTANT: This hook uses the generic docTypeListWithPagination path, which means:
 * - It does NOT automatically inject any special filters.
 * - If you need special filtering logic later, consider using
 *   erp.salesInvoices.listWithPagination directly (requires mapping
 *   filterDescriptors to Frappe format first), or adding filters via
 *   filterDescriptors.
 */
export function useSalesInvoiceListWithPagination(
  params: SalesInvoiceListHookParams
) {
  // Support both preMappedFilters (preferred) and filters (deprecated) for backwards compatibility
  const { preMappedFilters, filters, ...restParams } = params;
  const legacyFilters = preMappedFilters || filters;

  return useDocTypeListWithPagination<SalesInvoice>({
    doctype: "Sales Invoice",
    searchFields: ["name", "customer"],
    fields: params.fields ?? [...SALES_INVOICE_LIST_FIELDS], // Use provided fields or default
    listMethodOverride: "zaviago_backend.api.doc_list.list_with_system_fields", // Phase 2: Use method with comment counts
    ...restParams,
    // Map to the generic hook's parameter name
    filters: legacyFilters,
  });
}

export function useSalesInvoice(name: string) {
  return useQuery({
    queryKey: ["sales-invoices", "detail", name],
    queryFn: () => erp.salesInvoices.detail(name),
    enabled: !!name,
  });
}

